import { Injectable, inject } from '@angular/core'
import { ReportSpec } from '../../output/report/report-job'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import {
  IProductionListConfSelectDialogOptions,
  ProductionListConfSelectDialogComponent,
} from './conf/production-list-conf-select-dialog/production-list-conf-select-dialog.component'
import { OutputPreviewService } from '../../output/preview/output-preview.service'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { Interval } from '@elderbyte/ngx-starter'
import { Instant } from '@js-joda/core'
import { ControlTerminalMode } from '../../terminals/control-terminal-mode'
import { ProductionListScope } from './conf/prod-list-scope'

export interface ProductionListContext {
  scopes: ProductionListScope[]
  confId?: string
}

export class ProductionListBatchContext implements ProductionListContext {
  constructor(
    public batchId: string,
    public scopes: ProductionListScope[],
    public confId?: string
  ) {}
}

export class ProductionListCombinedContext implements ProductionListContext {
  constructor(
    public workCenterId: string,
    public interval: Interval,
    public timestamp: Instant,
    public mode: ControlTerminalMode,
    public scopes: ProductionListScope[],
    public workStationId?: string,
    public confId?: string
  ) {}
}

@Injectable({
  providedIn: 'root',
})
export class ProductionListPreviewService {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  private matDialog = inject(MatDialog)
  private readonly outputPreviewService = inject(OutputPreviewService)

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public previewProdList(ctx: ProductionListContext): void {
    if (!ctx.confId) {
      this.showProdListConfSelectDialog(ctx.scopes)
        .afterClosed()
        .subscribe((confId) => {
          if (confId) {
            ctx.confId = confId
            this.showProdListPreview(ctx)
          } else {
            this.log.info('No conf id selected!')
          }
        })
    } else {
      this.showProdListPreview(ctx)
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private showProdListConfSelectDialog(
    scopes: ProductionListScope[]
  ): MatDialogRef<ProductionListConfSelectDialogComponent> {
    return this.matDialog.open(ProductionListConfSelectDialogComponent, {
      minWidth: '30vw',
      minHeight: '30vh',
      data: <IProductionListConfSelectDialogOptions>{
        scopes: scopes,
      },
    })
  }

  private showProdListPreview(ctx: ProductionListContext): void {
    if (ctx instanceof ProductionListBatchContext) {
      this.previewBatch(ctx)
    } else if (ctx instanceof ProductionListCombinedContext) {
      this.previewCombined(ctx)
    } else {
      this.log.warn('Given context is unsupported!', ctx)
    }
  }

  private previewBatch(ctx: ProductionListContext): void {
    const batchCtx = ctx as ProductionListBatchContext

    if (this.validateBatchCtx(batchCtx)) {
      this.outputPreviewService.openPreviewDialog(
        ReportSpec.prodListReportForBatch(batchCtx.batchId, batchCtx.confId)
      )
    } else {
      this.log.warn('Not all required fields are set for ProdListBatchPreview!', batchCtx)
    }
  }

  private validateBatchCtx(ctx: ProductionListBatchContext) {
    return ctx.confId && ctx.batchId
  }

  private previewCombined(ctx: ProductionListContext): void {
    const combinedCtx = ctx as ProductionListCombinedContext

    if (this.validateCombinedCtx(combinedCtx)) {
      this.outputPreviewService.openPreviewDialog(
        ReportSpec.prodListReportCombined(
          combinedCtx.workCenterId,
          combinedCtx.workStationId,
          combinedCtx.interval,
          combinedCtx.timestamp,
          combinedCtx.mode,
          combinedCtx.confId
        )
      )
    } else {
      this.log.warn('Not all required fields are set for ProdListCombinedPreview!', combinedCtx)
    }
  }

  private validateCombinedCtx(ctx: ProductionListCombinedContext) {
    const liveModeValid = ctx.timestamp && ctx.mode === ControlTerminalMode.LIVE
    const previewModeValid = ctx.interval && ctx.mode === ControlTerminalMode.PREVIEW

    return ctx.confId && ctx.workCenterId && ctx.mode && (liveModeValid || previewModeValid)
  }
}
